// ---------------------------------------------------------------------------
// Shared GQL → domain mappers
//
// Centralised here to avoid duplication between workflowsGraphqlDataSource.ts
// and useWorkflowInstances.ts.  Both files import from this module.
// ---------------------------------------------------------------------------

import { TaskType as TaskTypeEnum } from "../types";
import type { GetControlTowerWorkflowsQuery } from "#graphql/supergraphTypes";
import type { TaskStepSummary, WorkflowInstanceRow } from "../types";

export type WorkflowGql = GetControlTowerWorkflowsQuery["controlTowerWorkflows"]["items"][0];
export type TaskGql = WorkflowGql["tasks"][0];

// BE returns every not-yet-active task as NOT_STARTED, which the UI shows as
// "Blocked". Surface workflow.currentStepName as the in-progress step so users
// can see which task is actionable.
export function effectiveStatus(
  rawStatus: string,
  taskName: string,
  currentStepName: string | null,
): string {
  if (rawStatus === "NOT_STARTED" && currentStepName && taskName === currentStepName) {
    return "IN_PROGRESS";
  }
  return rawStatus;
}

export function mapTask(
  task: TaskGql,
  index: number,
  currentStepName: string | null,
): TaskStepSummary {
  return {
    id: task.id,
    stepOrder: task.stepOrder ?? index,
    name: task.name,
    status: effectiveStatus(task.status, task.name, currentStepName),
    gpxStatus: task.gpxStatus,
    executionState: task.executionState,
    taskType: (task.taskType as TaskTypeEnum) ?? null,
    responsibleParty: task.responsibleParty ?? null,
    assignee: task.assignee
      ? { id: task.assignee.id, displayName: task.assignee.displayName }
      : null,
    dueDate: task.dueDate ?? null,
  };
}

export function mapWorkflow(wf: WorkflowGql): WorkflowInstanceRow {
  return {
    id: wf.id,
    sourceId: wf.sourceId,
    title: wf.title,
    templateName: wf.templateName ?? null,
    definitionScope: wf.definitionScope ?? null,
    definitionVersionNumber: wf.definitionVersionNumber ?? null,
    workflowType: wf.workflowType,
    status: wf.status,
    currentStepName: wf.currentStepName ?? null,
    currentAssignee: wf.currentAssignee
      ? { id: wf.currentAssignee.id, displayName: wf.currentAssignee.displayName }
      : null,
    clientId: wf.clientId ?? null,
    clientName: wf.clientName ?? null,
    entityId: wf.entityId ?? null,
    entityName: wf.entityName ?? null,
    client: wf.client ? { id: wf.client.id, name: wf.client.name } : null,
    entity: wf.entity ? { id: wf.entity.id, name: wf.entity.name } : null,
    createdAt: wf.createdAt ?? null,
    dueDate: wf.dueDate ?? null,
    slaStatus: wf.slaStatus as WorkflowInstanceRow["slaStatus"],
    progress: {
      completed: wf.progress?.completed ?? 0,
      total: wf.progress?.total ?? 0,
      percentage: wf.progress?.percentage ?? 0,
    },
    taskSteps: (wf.tasks ?? []).map((t, i) => mapTask(t, i, wf.currentStepName ?? null)),
    updatedAt: wf.updatedAt ?? null,
    source: wf.source ?? null,
    externalRefId: wf.externalRefId ?? null,
    faPreparer: wf.faPreparer ?? null,
    faReviewer: wf.faReviewer ?? null,
    isPreparer: wf.isPreparer ?? null,
    isReviewer: wf.isReviewer ?? null,
    requestedBy: wf.requestedBy ?? null,
    statusCategory: wf.statusCategory ?? null,
  };
}
